import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Typography,
} from '@mui/material';
import { useContext, useMemo, useRef, useState } from 'react';

import CommentsWrap from '@/components/comments_components/CommentsWrap';
import RequiredIndicator from '@/components/common/RequiredIndicator';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { QuestionProps } from '@/models/Question';

type HelperTextMap = Record<number, { helper: string }>;

function MultiCheckbox({ question }: { question: QuestionProps }) {
  const { answers, makingApiRequest, canEdit, saveApplicationAnswer2 } =
    useContext(CurrentApplicationContext);

  const { translatedText } = useContext(LocaleContext);

  const questionAnswer = answers[question.id];

  const initialChoices = questionAnswer?.choices?.map((x) => x.choiceId) ?? [];
  const [checked, setChecked] = useState<number[]>(initialChoices);
  const checkedRef = useRef<number[]>(initialChoices);

  const [loading, setLoading] = useState(false);

  const helperText = useMemo<HelperTextMap | null>(() => {
    if (question.questionNote == null) return null;
    try {
      return JSON.parse(question.questionNote) as HelperTextMap;
    } catch {
      return null;
    }
  }, [question.questionNote]);

  const isRequiredError = question.questionNeeded && (questionAnswer?.choices?.length ?? 0) === 0;

  const handleChanged = async (choiceId: number) => {
    const prev = checkedRef.current;
    const nextChecked = prev.includes(choiceId)
      ? prev.filter((x) => x !== choiceId)
      : [...prev, choiceId];

    checkedRef.current = nextChecked;
    setChecked(nextChecked);
    setLoading(true);
    try {
      await saveApplicationAnswer2(question, nextChecked);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display='flex' width='100%' justifyContent='space-between' alignItems='start'>
      <Box sx={{ display: 'flex' }}>
        <Box display='flex' alignItems='center'>
          <FormControl
            required={question.questionNeeded}
            error={isRequiredError}
            component='fieldset'
            variant='outlined'
            disabled={!canEdit || makingApiRequest || loading}
          >
            <Typography variant='adfBody1' sx={{ marginBottom: '8px' }}>
              {question.questionText}
              {question.questionNeeded ? <RequiredIndicator /> : null}
            </Typography>

            <FormGroup>
              {question.questionChoices?.map((que) => (
                <Box key={que.choiceId}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked.includes(que.choiceId)}
                        onChange={() => handleChanged(que.choiceId)}
                        name={String(que.choiceId)}
                      />
                    }
                    label={que.choice}
                  />

                  {helperText?.[que.choiceId]?.helper != null && (
                    <Typography marginLeft={4} variant='subtitle3' color='textSecondary'>
                      {helperText[que.choiceId].helper}
                    </Typography>
                  )}
                </Box>
              ))}
            </FormGroup>

            {isRequiredError && (
              <FormHelperText style={{ marginTop: '16px' }}>
                {translatedText.multiCheckbox.helper}
              </FormHelperText>
            )}
          </FormControl>

          {loading && (
            <Box
              sx={{
                display: 'inline-block',
                position: 'relative',
                top: '3px',
                width: 20,
                height: 20,
                ml: 1.25,
              }}
            >
              <CircularProgress size={22} />
            </Box>
          )}
        </Box>
      </Box>

      <CommentsWrap question={question} />
    </Box>
  );
}

export default MultiCheckbox;
